import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { propertyLacksDataSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksDataSectionMaker'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { DropdownSelectorPrePostFilter } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorPrePostFilter/DropdownSelectorPrePostFilter'
import { IconInfo } from 'components/icons/IconInfo'
import type { StackedChartSection, StackedChartSectionItem } from 'components/StackedChart/types'
import { translate } from 'i18n/i18n'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { makeNameEmitterTypeLabel } from 'utils/makeNameEmitterTypeLabel'
import {
  POST_FILTER_PRESSURE_VALUE_TYPE,
  PRE_FILTER_PRESSURE_VALUE_TYPE,
} from 'utils/prePostFilterValueTypes'
import { getAllPointsForPrePostFilter } from 'utils/setSelectedFieldAsset/_utils/getArgsForAnchorCoordinates'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { chooseAmongstUnAggHourlyAndDaily } from '../../../chooseAmongstUnAggHourlyAndDaily'
import { getPressureDifferentialData } from './_utils/getPressureDifferentialData'
import { processFilterPressureData } from './_utils/processFilterPressureData'

const checkPermission = () => selectedPropertyHasPermission({ permission: 'VIEW_IRRIGATION_DETAILS' })

export const prePostFiltersChart = ({
  data,
  selectedFieldAssets,
}: {
  data: routes.Values.Response
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): StackedChartSection | null => {
  const { property: propertyId, prePostFilterPoint } = selectedFieldAssets
  const { properties } = fieldAssetStore.getState()
  const property = properties?.[Number(propertyId)]
  const filterPoints = getAllPointsForPrePostFilter(Object.values(property?.points ?? {}))

  // the section only exists for properties that have a pre/post filter station
  if (!filterPoints.length) return null

  const commonReturnItems = {
    title: translate.phrases.banyanApp('Filter Pressure'),
    fieldAssetPicker: <DropdownSelectorPrePostFilter />,
    id: 'stackem-pre-post-filters',
  }

  if (!checkPermission()) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const selectedPoint = filterPoints.find((point) => point.lngLat === prePostFilterPoint) ?? filterPoints[0]

  if (chooseAmongstUnAggHourlyAndDaily({ excludeFuture: true }) === 'DAILY') {
    return {
      ...commonReturnItems,
      items: [],
      fullWidthItems: [
        {
          height: 100,
          content: (
            <div
              css={{
                alignItems: 'center',
                display: 'flex',
                fontSize: 14,
                gap: 5,
                justifyContent: 'center',
                padding: '10px 20px',
                width: '100%',
              }}
            >
              <span css={{ color: colors.primary, marginRight: 5, marginTop: '0.2em' }}>
                <IconInfo />
              </span>
              <span>
                {translate.phrases.banyanApp('Pressure values are only available for periods < 31 days')}
              </span>
            </div>
          ),
        },
      ],
    }
  }

  const timezone = getTimezoneForSelectedPropertyOrRegion({ properties, propertyId })
  const valuesForPoint = data?.points?.[selectedPoint.lngLat]?.values

  const preFilterData = processFilterPressureData({
    filterPressureData: valuesForPoint?.[PRE_FILTER_PRESSURE_VALUE_TYPE],
    timezone,
  })

  const postFilterData = processFilterPressureData({
    filterPressureData: valuesForPoint?.[POST_FILTER_PRESSURE_VALUE_TYPE],
    timezone,
  })

  if (!preFilterData.length && !postFilterData.length) {
    return propertyLacksDataSectionMaker(commonReturnItems)
  }

  const differentialData = getPressureDifferentialData({ preFilterData, postFilterData })
  const pressureUnit = unitConverter.irrigationPressure().suffix()
  const firstForecastTimestamp = +new Date()

  const seriesTooltip = {
    valueSuffix: ` ${pressureUnit}`,
    valueDecimals: 2,
  }

  const items: StackedChartSectionItem[] = [
    {
      chartConfig: {
        semiosHighchartsAdditions: {
          id: `pre-post-filters-${selectedPoint.lngLat}`,
          firstForecastTimestamp,
        },
        chart: { type: 'spline' as const, height: 250 },
        tooltip: { xDateFormat: '%Z' as const },
        series: [
          ...(preFilterData.length > 0
            ? [
                {
                  name: makeNameEmitterTypeLabel({
                    name: selectedPoint.name,
                    emitterType: 'pre-filter' as const,
                  }),
                  data: preFilterData,
                  color: colors.green,
                  type: 'spline' as const,
                  id: 'pre-filter',
                  tooltip: seriesTooltip,
                },
              ]
            : []),
          ...(postFilterData.length > 0
            ? [
                {
                  name: makeNameEmitterTypeLabel({
                    name: selectedPoint.name,
                    emitterType: 'post-filter' as const,
                  }),
                  data: postFilterData,
                  color: colors.orange,
                  type: 'spline' as const,
                  id: 'post-filter',
                  tooltip: seriesTooltip,
                },
              ]
            : []),
        ],
      },
    },
  ]

  if (differentialData.length > 0) {
    items.push({
      chartConfig: {
        semiosHighchartsAdditions: {
          id: `pre-post-filters-differential-${selectedPoint.lngLat}`,
          firstForecastTimestamp,
        },
        chart: { type: 'spline' as const, height: 150, marginTop: 35 },
        legend: { enabled: false },
        tooltip: { xDateFormat: '%Z' as const },
        series: [
          {
            name: translate.phrases.banyanApp('Pressure Differential (Pre - Post)'),
            data: differentialData,
            color: colors.midnight,
            type: 'spline' as const,
            id: 'differential',
            tooltip: seriesTooltip,
          },
        ],
      },
      childrenUpper: (
        <div css={{ fontWeight: 'bold', fontSize: '14px', padding: '10px 30px 0 30px' }}>
          {translate.phrases.banyanApp('Pressure Differential (Pre - Post)')}
        </div>
      ),
    })
  }

  return { ...commonReturnItems, items }
}
